import React from 'react';
import { ChevronDown } from 'lucide-react';
import { Task } from 'types/project/tasks';
import { User } from 'types/task';
import AddTaskTableLayer from '../ProjectDetails/AddTaskTableLayer';
import EmptyState from '../../../common/EmptyState';

interface StatusSummary {
  TO_DO: number;
  IN_PROGRESS: number;
  DONE: number;
}

interface ChildItemsTableProps {
  // Data
  filteredMappedTasks: Task[];
  childItems: any[];
  mappedUsers: User[];
  // Config
  projectId: string;
  projectKey: string;
  isCompact: boolean;
  isSubtaskMode: boolean;
  // Loading states
  isLoading: boolean;
  isReordering: boolean;
  isRemoving: boolean;
  isLoadingMore: boolean;
  // Counts
  childTotalElements: number;
  externalTotalCount?: number;
  statusSummary: StatusSummary;
  hasActiveFilters: boolean;
  activeFilters: any;
  // Handlers
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void;
  onAssigneeChange: (taskId: string, user: User | null) => void;
  onReporterChange: (taskId: string, user: User | null) => void;
  onStatusChange: (taskId: string, statusUuid: string) => void;
  onPriorityChange: (taskId: string, priority: string) => void;
  onRemove: (task: any) => void;
  onLoadMoreChildren?: () => void;
  onExpandChild?: (parentUuid: string) => void;
  onMoveToTop: (taskId: string) => void;
  onMoveToBottom: (taskId: string) => void;
  onReorderWorkItem?: (activeId: string, overId: string) => void;
  loadingParents?: Set<string>;
  onClearFilters: () => void;
  onStartCreate: () => void;
  readOnly?: boolean;
}

const ChildItemsTable: React.FC<ChildItemsTableProps> = ({
  filteredMappedTasks,
  childItems,
  mappedUsers,
  projectId,
  projectKey,
  isCompact,
  isSubtaskMode,
  isLoading,
  isReordering,
  isRemoving,
  isLoadingMore,
  childTotalElements,
  externalTotalCount,
  statusSummary,
  hasActiveFilters,
  activeFilters,
  onUpdateTask,
  onAssigneeChange,
  onReporterChange,
  onStatusChange,
  onPriorityChange,
  onRemove,
  onLoadMoreChildren,
  onExpandChild,
  onMoveToTop,
  onMoveToBottom,
  onReorderWorkItem,
  loadingParents,
  onClearFilters,
  onStartCreate,
  readOnly = false,
}) => {
  const totalDisplay = externalTotalCount ?? (childTotalElements || childItems.length);

  const renderEmptyState = () => {
    const isFilteringEverything =
      hasActiveFilters || (childItems.length > 0 && filteredMappedTasks.length === 0);

    if (isFilteringEverything) {
      const isHideCompletedOnly =
        activeFilters.hideCompleted &&
        activeFilters.statuses.length === 0 &&
        activeFilters.priorities.length === 0 &&
        activeFilters.taskTypes.length === 0 &&
        !activeFilters.reporters;

      return (
        <EmptyState
          title="No matching work items found"
          description={
            isHideCompletedOnly
              ? "All work items in this list are marked as completed. Toggle 'Hide done work items' to see them or try changing filters."
              : 'Try changing or clearing filters'
          }
          buttonLabel="Clear all filters"
          onButtonClick={onClearFilters}
          showAddIcon={false}
        />
      );
    }

    return (
      <EmptyState
        title={isSubtaskMode ? 'No subtasks yet' : 'No child issues yet'}
        description={
          isSubtaskMode
            ? 'Subtasks help you break down complex tasks into smaller, manageable steps.'
            : 'Child issues allow you to track related work items and maintain a clear hierarchy.'
        }
        buttonLabel={readOnly ? undefined : isSubtaskMode ? 'Add your first subtask' : 'Add child issue'}
        onButtonClick={readOnly ? undefined : onStartCreate}
        showAddIcon={!readOnly}
      />
    );
  };

  return (
    <div className="mb-3 border border-primary-subtle rounded-lg overflow-hidden bg-primary-variant transition-all">
      {/* Status summary bar */}
      <div className="px-4 py-2 bg-primary/40 border-b border-primary-subtle flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            <span className="text-[11px] font-semibold text-neutral-medium uppercase tracking-wider">
              {statusSummary.TO_DO} Open
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="text-[11px] font-semibold text-neutral-medium uppercase tracking-wider">
              {statusSummary.IN_PROGRESS} In Progress
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span className="text-[11px] font-semibold text-neutral-medium uppercase tracking-wider">
              {statusSummary.DONE} Done
            </span>
          </div>
        </div>
        <span className="text-[11px] font-medium text-neutral-medium/50 italic">
          {totalDisplay} {totalDisplay === 1 ? 'item' : 'items'} total
        </span>
      </div>

      {/* Task table */}
      <AddTaskTableLayer
        tasks={filteredMappedTasks}
        onUpdateTask={onUpdateTask}
        onCreateTask={() => {}}
        assigneeOptions={mappedUsers}
        reporterOptions={mappedUsers}
        onAssigneeChange={onAssigneeChange}
        onReporterChange={onReporterChange}
        onStatusChange={onStatusChange}
        onPriorityChange={onPriorityChange}
        onReorderWorkItem={onReorderWorkItem}
        projectId={projectId}
        projectKey={projectKey}
        isPending={isLoading || isReordering || false}
        isRemoving={isRemoving}
        onRemove={readOnly ? undefined : onRemove}
        onLoadMoreChildren={onExpandChild}
        onMoveToTop={readOnly || hasActiveFilters || activeFilters?.sortBy !== 'listOrder' ? undefined : onMoveToTop}
        onMoveToBottom={readOnly || hasActiveFilters || activeFilters?.sortBy !== 'listOrder' ? undefined : onMoveToBottom}
        isReadOnly={readOnly}
        loadingParents={loadingParents}
        visibleColumns={
          isCompact
            ? ['workItemKey', 'title', 'workStatusUuid', 'workItemAssigneeAppUserUuid']
            : ['workItemKey', 'title', 'workStatusUuid', 'workItemAssigneeAppUserUuid', 'workItemReporterAppUserUuid', 'workPriority']
        }
        isCompact={isCompact}
        isScrollable={false}
        renderEmptyState={renderEmptyState}
      />

      {/* Load more button */}
      {childTotalElements > childItems.length && (
        <div className="px-4 py-3 border-t border-primary-subtle flex justify-center bg-primary/20">
          <button
            onClick={onLoadMoreChildren}
            disabled={isLoadingMore}
            className="text-[11px] font-semibold text-blue-400 hover:text-blue-300 transition-all duration-200 flex items-center gap-2 tracking-wide bg-blue-500/10 hover:bg-blue-500/20 px-5 py-2.5 rounded-full group disabled:opacity-50 border border-blue-500/20"
          >
            {isLoadingMore ? (
              <>
                <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Loading more...</span>
              </>
            ) : (
              <>
                <span>Show {childTotalElements - childItems.length} more child items</span>
                <ChevronDown size={15} className="group-hover:translate-y-0.5 transition-transform" />
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ChildItemsTable;
